import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/store';
import { userBrandsList } from '@/store/slices/casino-instance';
import { OWNERS_HUB_ROUTES } from '@/constants/routes';
import { asset } from '../../../../lib/asset';
import { Logo } from '../../../../components/wizard';
import { useDeviceAnchor, usePreviewClip } from '../../../../components/preview-stage';
import { useConfigurator } from '../../../../store/configurator';
import { findThemeScreenshot } from '../../../../data/preview';
import { usePreviewSlot } from '../../../../lib/staticPreview';
import { useIsTablet } from '../../../../lib/deviceType';
import { LAPTOP_FRAME_FINISH } from '../DevicePreview';
import { StaticMacDevice, StaticPhoneDevice } from '../DevicePreview/StaticDevices';
import {
  Root,
  Bloom,
  TopBar,
  Glass,
  CopyColumn,
  Heading,
  Verify,
  PlaneWrap,
  PlaneImg,
  CloseHint,
  LaunchCopy,
  StudioButton,
  StudioButtonBottom,
  StudioButtonLabel,
  ReturningColumn,
  SupportDivider,
  SupportBlock,
  SupportNeedHelp,
  SupportBlockEmail,
  UserMenuEdgeAnchor,
  EmailColumn,
  HeadGroup,
  EmailChip,
  ChipEmail,
  CloseSupportGroup,
  PreviewColumn,
  PreviewCaption,
  DeviceStage,
  PhoneOnStage,
  MacFill,
  PhoneFill,
  StaticPhoneFill,
  OpenMailButton,
  OpenMailLabel,
  PREVIEW_INSET,
  LAPTOP_TOP,
  LAPTOP_MAX_H,
} from './PaymentSuccess.styles';

export type PaymentSuccessProps = {
  /** Buyer email - shown as the inbox we sent verification to. */
  email: string;
  /**
   * Whether the money has actually ARRIVED (the session reached a post-payment status).
   *
   * SPEI is an asynchronous rail: the buyer makes the transfer in their own banking app and it
   * settles minutes to a business day later, so this screen is normally reached BEFORE the
   * funds land. `false` (the usual case) says the transfer is registered and pending; `true`
   * says it is confirmed. The heading and lead copy switch on it so we never claim a payment
   * that has not cleared.
   */
  settled: boolean;
  /**
   * The backend's verdict from `POST /session/payment-acknowledged`, when we have it.
   *
   * `canContinue: true` means the session is already on `customization`, so the owner can go
   * straight into onboarding. `false` means a login-link email was sent instead and the session
   * only advances once that link is opened, so the screen must point them at their inbox.
   *
   * This OVERRIDES the websocket/ownership signals below: it is the authoritative answer to the
   * exact question this screen asks, from the call that DECIDED whether a login link was sent.
   * `null` when the call failed or hasn't happened, in which case those signals still apply.
   */
  acknowledgement?: { canContinue: boolean; emailVerificationRequired: boolean } | null;
};

/**
 * Post-checkout confirmation (desktop + mobile).
 *
 * TWO axes, both driven by the backend rather than a client-side guess:
 *
 * 1. **Settled or pending** (`settled`) - whether the SPEI transfer has actually arrived. The
 *    flow lands here as soon as the buyer says they made the transfer, so the default copy is
 *    "Transfer registered … we'll confirm by email"; it becomes "Payment successful" if the
 *    session status flips while they are still on the page.
 * 2. **Whether a verification email went out** (`session.postPayment`, set by the WS from
 *    `email_verification_required`):
 *    - **Verification needed** ('success-mail' | 'mail') - a login-link email was sent, so we
 *      show the inbox chip + paper-plane (the buyer must verify before personalizing); mobile
 *      shows an "Open Email App" CTA.
 *    - **Already verified** ('success-redirect' | 'redirect') - no email was sent, so we offer
 *      a "Continue to Owner Studio" button straight into onboarding (the customizer) - the same
 *      place the verification path lands once it verifies via the emailed link.
 *
 * The right column is the laptop+phone device preview of the configured site
 * (desktop only) in every variant.
 */
/** Envelope glyph for the email chip - inherits the chip's colour via currentColor. */
const ENVELOPE_GLYPH = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
    <rect x="1.75" y="3.25" width="12.5" height="9.5" rx="2.5" stroke="currentColor" strokeWidth="1.3" />
    <path
      d="m2.5 4.75 5.5 4 5.5-4"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const PaymentSuccess = ({ email, settled, acknowledgement = null }: PaymentSuccessProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  // FALLBACK path only (a device with no static screenshot, see below): the laptop +
  // phone previews are then floated by the persistent host (the same two warm iframes the
  // build flow used). These elevate the host above the opaque success panel; the anchors
  // fill their slots inside the centred DeviceStage and the host dresses them with bezels.
  // `clip` cuts them to the preview pane so a laptop wider than a narrow pane is trimmed at
  // the pane edge (the floated host layer is viewport-fixed, so the pane's own overflow
  // can't). Registered unconditionally so the hook order stays stable; only ATTACHED to an
  // anchor element when that device is exhausted.
  const macRef = useDeviceAnchor({ role: 'mac', mode: 'group', bezel: 'macFinish', elevate: true, clip: true });
  const phoneRef = useDeviceAnchor({
    role: 'phone',
    mode: 'group',
    bezel: 'phone',
    phoneZoomWidth: 440,
    elevate: true,
    clip: true,
  });
  // Publishes the preview pane as the clip region the host cuts the floated devices to,
  // and is measured below to place the device group horizontally.
  const previewClipRef = usePreviewClip();
  const paneRef = React.useRef<HTMLDivElement | null>(null);
  const setPaneRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      paneRef.current = node;
      previewClipRef(node);
    },
    [previewClipRef],
  );

  // Static screenshot of the configured site, same source as the Finish step's
  // DevicePreview: the backend pre-renders a desktop + iphone capture per (theme,
  // colour, art-style, locale). Show those by default; only when none exists for the
  // selection (or the image 404s) does a device fall back to the live warm iframe via
  // the anchors below. Reads the persisted selection from the configurator store — still
  // populated post-payment (same provider scope), and gracefully null on a cold restore,
  // which just routes both devices to the live fallback.
  const { selectedTheme, selectedColor, selectedStyle } = useConfigurator();
  const lang = (i18n.language || 'en').split('-')[0];
  const screenshot = findThemeScreenshot(selectedTheme, selectedColor, selectedStyle, lang);
  const mac = usePreviewSlot(screenshot?.desktop ?? '');
  const phone = usePreviewSlot(screenshot?.iphone ?? '');

  // On tablets the persistent iframe host is suppressed (it crashes iPad Safari), so a
  // device with no static screenshot stays a static device (flat brand fill) instead of
  // falling back to an anchor the now-absent host would never float a live iframe over.
  const isTablet = useIsTablet();

  // Horizontal placement of the device group: centre it WHILE it fits, but once the
  // laptop is wider than the pane pin it to the left inset so it bleeds off the RIGHT
  // (clipped by the pane) — so the phone at the laptop's lower-left ALWAYS stays in frame
  // and only the laptop is cropped, never the phone. Mirrors the CSS height cap
  // (LAPTOP_MAX_H) so the measured width matches what actually renders. (Vertical cap +
  // centring is pure CSS on DeviceStage.)
  const [groupLeft, setGroupLeft] = React.useState(PREVIEW_INSET);
  React.useEffect(() => {
    const el = paneRef.current;
    if (!el) return undefined;
    const [ratioW, ratioH] = LAPTOP_FRAME_FINISH.aspectRatio.split('/').map((n) => parseFloat(n));
    const laptopRatio = ratioW / ratioH;
    const compute = (): void => {
      const laptopHeight = Math.min(el.clientHeight - LAPTOP_TOP - PREVIEW_INSET, LAPTOP_MAX_H);
      const laptopWidth = laptopHeight * laptopRatio;
      setGroupLeft(Math.max(PREVIEW_INSET, (el.clientWidth - laptopWidth) / 2));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Show the address the backend actually mails to - the one persisted to the
  // session at checkout (the buyer's possibly-edited email) - falling back to the
  // prop. Keeps desktop + mobile consistent with what was sent.
  const sessionEmail = useAppSelector((s) => s.session.data.configuration_data?.emailAddress);
  const shownEmail = sessionEmail || email;

  // Whether a verification email was sent is the BACKEND's call, not ours: after the
  // charge the WS `session_update` carries `email_verification_required`, mapped to
  // `session.postPayment` ('success-mail' | 'mail' => an email WAS sent; 'success-
  // redirect' | 'redirect' => the buyer is already verified, so NO email - send them
  // straight on). The success copy must track whether mail actually went out, so we
  // render off this signal rather than guessing from prior ownership (which left the
  // "verify your email" screen showing for an already-authenticated first-brand buyer
  // who never received - and never needed - a mail).
  const postPayment = useAppSelector((s) => s.session.postPayment);
  const emailSent = postPayment === 'success-mail' || postPayment === 'mail';
  const noEmail = postPayment === 'success-redirect' || postPayment === 'redirect';

  // Fallback ONLY when the WS signal is absent (e.g. a cold 3DS-redirect return that
  // restored `status` via sessionLoad without a `postPayment`): approximate it from
  // prior ownership - a returning owner is already verified. Self-fetch the brand list
  // once if we arrived cold so the fallback stays reliable.
  const dispatch = useAppDispatch();
  const hasBrands = useAppSelector((s) => s.casinoInstance.brands.length > 0);
  const brandsLoaded = useAppSelector((s) => s.casinoInstance.loaded);
  const brandsLoading = useAppSelector((s) => s.casinoInstance.loading);
  const requestedBrands = React.useRef(false);
  React.useEffect(() => {
    if (requestedBrands.current) return;
    requestedBrands.current = true;
    if (!brandsLoaded && !brandsLoading) dispatch(userBrandsList());
  }, [dispatch, brandsLoaded, brandsLoading]);

  // `returning` => the no-email "Continue to Owner Studio" variant.
  //
  // Precedence, most authoritative first:
  //  1. `/payment-acknowledged` — it answers this exact question (`can_continue`) and it is the
  //     call that DECIDED whether a login link was sent, so nothing can contradict it;
  //  2. the websocket post-payment signal;
  //  3. prior ownership — a returning owner is already verified.
  const returning =
    acknowledgement != null
      ? acknowledgement.canContinue
      : noEmail
        ? true
        : emailSent
          ? false
          : brandsLoaded && hasBrands;

  const navigate = useNavigate();
  // Straight into onboarding (the customizer: region → name & domain → logo → deploy)
  // to set up the just-purchased brand - the same destination the first-casino path
  // reaches after verifying via the emailed /login link (resolvePostAuthRoute routes a
  // payment_successful session to STUDIO_CONFIGURATOR). Matches the flow PDF.
  const goToStudio = React.useCallback(() => navigate(OWNERS_HUB_ROUTES.STUDIO_CONFIGURATOR), [navigate]);

  return (
    <Root>
      <Bloom $img={asset('payment/success-bloom-mobile.jpg')} />
      {/* Both variants: pull the wizard's global account icon to the corner (Figma);
          "Need help?" lives at the bottom of the copy column, not the header. */}
      <UserMenuEdgeAnchor />

      <TopBar>
        <Logo />
      </TopBar>

      <Glass>
        <CopyColumn>
          {returning ? (
            <ReturningColumn>
              <Heading text={settled ? t('Payment successful') : t('Transfer registered')} tag="h1" />
              <LaunchCopy
                text={
                  settled
                    ? t('You can now personalize your new brand and take it live by visiting Owner Studio.')
                    : t(
                        "We'll confirm by email as soon as your SPEI transfer arrives. You can start personalizing your brand in Owner Studio right away.",
                      )
                }
                tag="paragraph"
              />
              <StudioButton type="button" onClick={goToStudio} data-ga-id="cfg-finish-success-studio">
                <StudioButtonLabel text="Continue to Owner Studio" tag="span" />
              </StudioButton>
              <SupportDivider />
              <SupportBlock>
                <SupportNeedHelp text="Need help? " tag="span" />
                <SupportBlockEmail text="support@whitelabels.com" tag="span" />
              </SupportBlock>
            </ReturningColumn>
          ) : (
            <EmailColumn>
              <HeadGroup>
                <Heading text={settled ? t('Payment successful') : t('Transfer registered')} tag="h1" />
                <EmailChip>
                  {ENVELOPE_GLYPH}
                  <ChipEmail text={shownEmail || t('your inbox')} tag="span" />
                </EmailChip>
              </HeadGroup>
              <Verify
                text={
                  settled
                    ? t('Please verify your email to continue with personalizing and taking your website live.')
                    : t(
                        "Please verify your email to continue with personalizing and taking your website live. We'll confirm separately once your SPEI transfer arrives.",
                      )
                }
                tag="paragraph"
              />
              <PlaneWrap>
                <PlaneImg src={asset('payment/paper-plane-trail.svg')} alt="" />
              </PlaneWrap>
              <SupportDivider />
              <CloseSupportGroup>
                <CloseHint
                  text="You can safely close this page. You’ve got everything you need in your inbox."
                  tag="paragraph"
                />
                <SupportBlock>
                  <SupportNeedHelp text="Need help? " tag="span" />
                  <SupportBlockEmail text="support@whitelabels.com" tag="span" />
                </SupportBlock>
              </CloseSupportGroup>
            </EmailColumn>
          )}
        </CopyColumn>

        <PreviewColumn ref={setPaneRef}>
          {/* Both variants share this caption per the Figma (the email-variant frame
              4701-1420 uses the same "waiting" copy as the returning frame). */}
          <PreviewCaption text={t('Your new iGaming website is waiting')} tag="paragraph" />
          <DeviceStage $left={groupLeft}>
            {mac.exhausted && !isTablet ? (
              <MacFill ref={macRef} />
            ) : (
              <StaticMacDevice
                imageSrc={mac.src}
                onImageError={mac.onError}
                topColor={selectedColor?.topNav}
                bgColor={selectedColor?.bg}
                frame={LAPTOP_FRAME_FINISH}
                spinner={!mac.exhausted}
              />
            )}
            <PhoneOnStage>
              {phone.exhausted && !isTablet ? (
                <PhoneFill ref={phoneRef} />
              ) : (
                <StaticPhoneFill>
                  <StaticPhoneDevice
                    imageSrc={phone.src}
                    onImageError={phone.onError}
                    topColor={selectedColor?.topNav}
                    bottomColor={selectedColor?.bottomNav}
                    bgColor={selectedColor?.bg}
                    spinner={!phone.exhausted}
                  />
                </StaticPhoneFill>
              )}
            </PhoneOnStage>
          </DeviceStage>
        </PreviewColumn>
      </Glass>

      {/* Mobile-only bottom CTA - returning owners jump to Owner Studio; first-time
          owners open their mail app to verify (desktop shows the inline button /
          close hint instead). */}
      {returning ? (
        <StudioButtonBottom type="button" onClick={goToStudio} data-ga-id="cfg-finish-success-studio-mobile">
          <StudioButtonLabel text="Continue to Owner Studio" tag="span" />
        </StudioButtonBottom>
      ) : (
        <OpenMailButton href="mailto:">
          <OpenMailLabel text="Open Email App" tag="span" />
        </OpenMailButton>
      )}
    </Root>
  );
};

export default PaymentSuccess;
